import type { HelpArticle } from "./contracts.js";
import bundledContent from "../help/dataglow-help-2.6.0.json" with { type: "json" };

/** Content only: packages contain no HTML renderer, scripts, URLs or executable assets. */
export interface HelpPackage {
  manifest: {
    schemaVersion: 1;
    packageId: string;
    title: string;
    productVersion: string;
    release: string;
    revision: string;
    defaultTopic: string;
  };
  index: string[];
  contextMap: Record<string, string>;
  articles: HelpArticle[];
}
export interface ActiveHelpPackage extends HelpPackage {
  source: "bundled" | "installed";
  fallbackReason?: string;
}
export interface HelpPackageList {
  activeId: string;
  previousId: string | null;
  packages: {
    id: string;
    manifest: HelpPackage["manifest"];
    source: "bundled" | "installed";
    installedAt?: string;
  }[];
  fallbackReason?: string;
}
export const HELP_PACKAGE_MAX_BYTES = 1024 * 1024;
export const bundledHelp = bundledContent as HelpPackage;
/** Kept for existing imports and offline fallback. The Help panel fetches runtime content. */
export const helpArticles: HelpArticle[] = bundledHelp.articles;
export function helpPackageId(pkg: HelpPackage): string {
  return `${pkg.manifest.packageId}@${pkg.manifest.revision}`;
}
export function resolveHelp(
  id: string,
  pkg: HelpPackage = bundledHelp,
): { article: HelpArticle; exact: boolean; requestedContext: string } {
  const lookup = (key: string) => {
    const mapped = Object.hasOwn(pkg.contextMap, key) ? pkg.contextMap[key] : key;
    return pkg.articles.find((article) => article.id === mapped);
  };
  let article = lookup(id);
  if (article) return { article, exact: true, requestedContext: id };
  // Stable field contexts use dots; a missing field can use its nearest parent.
  let parent = id;
  while (parent.includes(".")) {
    parent = parent.slice(0, parent.lastIndexOf("."));
    article = lookup(parent);
    if (article) return { article, exact: false, requestedContext: id };
  }
  return {
    article: lookup(pkg.manifest.defaultTopic) || pkg.articles[0],
    exact: false,
    requestedContext: id,
  };
}
export function findHelp(id: string, pkg: HelpPackage = bundledHelp): HelpArticle {
  return resolveHelp(id, pkg).article;
}
export function searchHelp(query: string, pkg: HelpPackage = bundledHelp): HelpArticle[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  return pkg.articles.filter((article) => {
    const haystack = `${article.title} ${article.summary} ${article.category} ${article.steps.join(" ")} ${(article.fields ?? []).map((field) => `${field.name} ${field.description} ${field.example ?? ""}`).join(" ")} ${(article.troubleshooting ?? []).map((issue) => `${issue.symptom} ${issue.resolution}`).join(" ")}`.toLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
}
